import React from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";

const DYNAMIC_PERIOD_FIELD = "periodo_dinamico_mensal";

function updateField(setFormState, field, value) {
  setFormState((prev) => ({ ...prev, [field]: value }));
}

const ErpFormField = ({
  input,
  formState,
  setFormState,
  moduleDateFieldNames = [],
  error = false,
  helperText = "",
}) => {
  const [chipsInputValue, setChipsInputValue] = React.useState("");

  if (!input || input.type === "hidden") return null;

  const field = input.name;
  const label = input.label || input.name;
  const value = formState[field] || "";
  const isDynamicMonthlyPeriodEnabled = formState[DYNAMIC_PERIOD_FIELD] === "sim";
  const isEnabledByRule = !input.enabledWhen
    || formState[input.enabledWhen.field] === input.enabledWhen.equals;

  const handleBasicChange = (selectedValue) => {
    if (input.syncField) {
      const mapped = input.syncField.map?.[selectedValue];
      setFormState((prev) => ({
        ...prev,
        [field]: selectedValue,
        ...(mapped !== undefined ? { [input.syncField.target]: mapped } : {}),
      }));
      return;
    }

    if (field === DYNAMIC_PERIOD_FIELD) {
      setFormState((prev) => ({
        ...prev,
        [field]: selectedValue,
        ...(selectedValue === "sim"
          ? Object.fromEntries(
              moduleDateFieldNames.filter((key) =>
                Object.prototype.hasOwnProperty.call(prev, key),
              ).map((key) => [key, ""]),
            )
          : {}),
      }));
      return;
    }

    updateField(setFormState, field, selectedValue);
  };

  const renderLabel = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#424242" }}>
        {label}
      </Typography>

      {input.helpText ? (
        <Tooltip
          arrow
          placement="right"
          title={input.helpText}
          slotProps={{
            tooltip: {
              sx: {
                fontSize: 13,
                lineHeight: 1.4,
                maxWidth: 280,
              },
            },
          }}
        >
          <HelpOutlineRoundedIcon
            sx={{ fontSize: 17, color: "#64748B", cursor: "pointer" }}
          />
        </Tooltip>
      ) : null}
    </Box>
  );

  const renderFieldByType = () => {
    if (input.type === "date") {
      return (
        <TextField
          fullWidth
          type="date"
          disabled={
            (isDynamicMonthlyPeriodEnabled &&
            moduleDateFieldNames.includes(field))
            || !isEnabledByRule
          }
          value={value}
          onChange={(event) => handleBasicChange(event.target.value)}
          InputLabelProps={{ shrink: true }}
          error={error}
          helperText={helperText}
        />
      );
    }

    if (input.type === "radio") {
      return (
        <FormControl error={error}>
          <RadioGroup
            row
            value={value || "nao"}
            onChange={(event) => handleBasicChange(event.target.value)}
          >
            {(input.options || []).map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
                sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
              />
            ))}
          </RadioGroup>
          {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
        </FormControl>
      );
    }

    if (input.type === "select") {
      return (
        <FormControl fullWidth error={error}>
          <Select
            value={value}
            onChange={(event) => handleBasicChange(event.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Selecione
            </MenuItem>
            {(input.options || []).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
        </FormControl>
      );
    }

    if (input.type === "chips") {
      const chipsValue = Array.isArray(value)
        ? value
        : typeof value === "string" && value.trim()
          ? value.split(",").map((item) => item.trim()).filter(Boolean)
          : [];

      const commitTokens = (rawInput, keepLastAsDraft = false) => {
        const source = String(rawInput || "");
        const parts = source.split(",");

        const draft = keepLastAsDraft ? parts.pop() || "" : "";
        const tokens = parts
          .map((item) => item.trim())
          .filter(Boolean);

        if (tokens.length > 0) {
          handleBasicChange([...chipsValue, ...tokens]);
        }

        setChipsInputValue(draft);
      };

      return (
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={chipsValue}
          inputValue={chipsInputValue}
          onInputChange={(_, newInputValue, reason) => {
            if (reason !== "input") {
              setChipsInputValue(newInputValue || "");
              return;
            }

            if (newInputValue.includes(",")) {
              commitTokens(newInputValue, true);
              return;
            }

            setChipsInputValue(newInputValue);
          }}
          onChange={(_, newValue) => {
            const normalized = newValue
              .map((item) =>
                typeof item === "string" ? item.trim() : String(item).trim(),
              )
              .filter(Boolean);

            handleBasicChange(normalized);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              placeholder="Digite e use vírgula, espaço ou Enter"
              onKeyDown={(event) => {
                const current = String(chipsInputValue || "").trim();
                if (!current) return;

                if (event.key === " " || event.key === "Enter" || event.key === ",") {
                  event.preventDefault();
                  const tokens = current
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean);

                  if (tokens.length > 0) {
                    handleBasicChange([...chipsValue, ...tokens]);
                    setChipsInputValue("");
                  }
                }
              }}
              error={error}
              helperText={helperText}
            />
          )}
        />
      );
    }

    if (input.type === "multi_select") {
      const normalizedOptions = (input.options || []).map((option) => ({
        value: String(
          option?.value ?? option?.id ?? option?.key ?? option?.code ?? "",
        ),
        label:
          option?.label
          || option?.description
          || option?.motivo
          || option?.descricao
          || option?.name
          || option?.nome
          || String(option?.value ?? option?.id ?? ""),
      }));
      const allOptionValue = "__ALL__";
      const options = [{ value: allOptionValue, label: "Selecionar todos" }, ...normalizedOptions];
      const selectedValues = Array.isArray(value)
        ? value.map((item) => String(item))
        : [];
      const selectedOptions = selectedValues.map((selectedValue) =>
        options.find((option) => option.value === selectedValue)
        || { value: selectedValue, label: selectedValue },
      );

      return (
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={options}
          value={selectedOptions}
          getOptionLabel={(option) => option.label || option.value}
          isOptionEqualToValue={(option, selected) =>
            option.value === selected.value
          }
          onChange={(_, newValue) => {
            const hasAll = newValue.some((item) => item.value === allOptionValue);

            if (hasAll) {
              const allValues = normalizedOptions.map((item) => item.value);
              const isAllAlreadySelected =
                allValues.length > 0
                && allValues.every((optionValue) => selectedValues.includes(optionValue));

              handleBasicChange(isAllAlreadySelected ? [] : allValues);
              return;
            }

            handleBasicChange(
              newValue
                .map((item) => item.value)
                .filter((optionValue) => optionValue !== allOptionValue),
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              placeholder={
                selectedValues.length === 0
                  ? (input.placeholder || "Selecione um ou mais itens")
                  : ""
              }
              error={error}
              helperText={helperText}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {input.loadingOptions ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      );
    }

    return (
      <TextField
        fullWidth
        value={value}
        onChange={(event) => handleBasicChange(event.target.value)}
        placeholder={label}
        error={error}
        helperText={helperText}
      />
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {renderLabel()}
      {renderFieldByType()}
    </Box>
  );
};

export default ErpFormField;
